import { observer } from "mobx-react-lite";
import { useAppStore } from "@/store";
import { useMemo } from "react";
import { NotesIndexTuple } from "@/store/note";
import { MdsVirtualizedItemList } from "@/design-system/components/item-list";
import {
  MdsItemListRowData,
  MdsItemListSize,
  MdsItemListRowType,
} from "@/design-system/components/item-list/types";
import { getRowForNoteObservable } from "@/store/note/getRowForNoteObservable";
import { groupLens, LensListItem } from "@/modules/timeline";
import { MDSText, MDSTextColor } from "@/mds";
import styled from "@emotion/styled";

export interface RecentsListProps {}

export const RecentsList = observer<RecentsListProps>(function RecentItems() {
  const { store, pageStore } = useAppStore();
  const state = pageStore.quickSearchModal;

  /**
   * We use state here [instead of observing the store] because we don't want to
   * update the UI while the recents UI while this component is open.
   *
   * (e.g. if someone selects a Note, we don't want the recent items list to update
   * while the list is fading out).
   */
  const recentItems = useMemo(() => {
    const recentItems = store.recentItems.sortedRecentNotesInteractedWithByMe;
    state.setOrderedItemIds(recentItems.map(note => note.id));
    return recentItems;

    /** This is intentional. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const itemRows: MdsItemListRowData[] = useMemo(() => {
    const slicedItems: LensListItem<NotesIndexTuple>[] = recentItems.slice(0, 100).map(note => ({
      dateTime: new Date(note.lastViewedAt),
      item: [1, note.lastViewedAt, note.id],
    }));

    if (slicedItems.length === 0) {
      return [
        {
          key: "header",
          type: MdsItemListRowType.SectionHeader,
          payload: { title: "Recent" },
        },
        {
          key: "empty-list",
          type: MdsItemListRowType.Other,
          payload: {
            children: (
              <EmptyListRowWrapper>
                <MDSText color={MDSTextColor.Tertiary}>No recently opened notes</MDSText>
              </EmptyListRowWrapper>
            ),
          },
        },
      ];
    }

    const groupedLensItems = groupLens(slicedItems);

    const output: MdsItemListRowData[] = [];
    for (const group of groupedLensItems) {
      output.push({
        type: MdsItemListRowType.SectionHeader,
        key: group.title,
        payload: { title: group.title },
      });

      for (const item of group.items) {
        const tuple = item.item;
        output.push({
          type: MdsItemListRowType.AsyncNote,
          key: tuple.length === 4 ? tuple[3] : tuple[2],
          size: MdsItemListSize.Medium,
          payload: {
            noteId: tuple.length === 4 ? tuple[3] : tuple[2],
            itemRow: note =>
              getRowForNoteObservable({
                dateTime: item.dateTime,
                inMainPanel: true,
                noteObservable: note,
                listState: state.listState,
                store,
                disablePreview: true,
                trackClick: state.close,
              }),
          },
        });
      }
    }

    return output;
  }, [recentItems, state.listState, store, state.close]);

  return <MdsVirtualizedItemList rows={itemRows} />;
});

const EmptyListRowWrapper = styled.div`
  line-height: ${({ theme }) => theme.lineHeights["text-s"]};
  padding-inline: ${({ theme }) => theme.spacing["7"]};
  padding-block: ${({ theme }) => theme.spacing["4"]};
`;
